import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import { MeResponse, StaffListResponse, StaffMember } from "../types";

interface Props {
  me: MeResponse;
}

interface NotifPref {
  mode: string;
  quiet_from: string | null;
  quiet_to: string | null;
  min_severity: string;
  last_seen_at: string | null;
  last_reminder_sent_at: string | null;
  available_modes: string[];
  available_severities: string[];
}

export function SettingsPage({ me }: Props) {
  const [pref, setPref] = useState<NotifPref | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<NotifPref>("/api/notifications").then(setPref).catch((e) =>
      setErr(String(e)),
    );
  }, []);

  const patchPref = async (body: Record<string, unknown>) => {
    setSaving(true);
    setErr(null);
    try {
      const r = await api.patch<NotifPref>("/api/notifications", body);
      setPref(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };
  const setMode = (mode: string) => patchPref({ mode });

  return (
    <div>
      <h1 className="text-xl font-semibold mb-3">⚙️ Профиль</h1>

      <section className="bg-tg-sectionBg rounded-xl p-4 mb-4">
        <div className="text-xs text-tg-hint">Telegram ID</div>
        <div className="text-base font-mono">{me.tg_id}</div>
        <div className="text-xs text-tg-hint mt-3">Имя</div>
        <div className="text-base">{me.display_name || "—"}</div>
        <div className="text-xs text-tg-hint mt-3">Роли</div>
        <div className="text-base">
          {me.roles.length ? me.roles.join(", ") : "—"}
        </div>
      </section>

      <section className="bg-tg-sectionBg rounded-xl p-4 mb-4">
        <div className="text-sm font-medium mb-2">Что вам доступно</div>
        <ul className="text-sm space-y-1">
          <Item ok={me.permissions.payments_view} text="Просмотр реестра платежей" />
          <Item ok={me.permissions.payments_write} text="Отметка «оплачено»" />
          <Item ok={me.permissions.payments_confirm} text="Подтверждение по выписке" />
          <Item
            ok={me.permissions.payments_full_details}
            text="Расширенная карточка заявки"
          />
          <Item ok={me.permissions.analytics} text="Аналитика" />
          <Item ok={me.permissions.audit} text="Аудит" />
        </ul>
      </section>

      <section className="bg-tg-sectionBg rounded-xl p-4">
        <div className="text-sm font-medium mb-1">Уведомления в чат</div>
        <div className="text-xs text-tg-hint mb-3">
          Когда вы выполняете действие через mini-app (отметили оплату,
          подтвердили по выписке), нужны ли пушу в личку с ботом?
        </div>

        {err && (
          <div className="mb-2 p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
            {err}
          </div>
        )}

        <div className="space-y-2">
          <ModeRadio
            label="Всегда дублировать"
            description="Каждое действие — push в чат, как раньше."
            value="chat_always"
            current={pref?.mode}
            disabled={saving}
            onClick={() => setMode("chat_always")}
          />
          <ModeRadio
            label="Не дублировать"
            description="Работаю через mini-app. Бот пришлёт дайджест только если у меня скопились незакрытые задачи 30+ мин и я не открывал приложение."
            value="chat_never"
            current={pref?.mode}
            disabled={saving}
            onClick={() => setMode("chat_never")}
          />
        </div>

        {pref?.last_reminder_sent_at && (
          <div className="text-[11px] text-tg-hint mt-3">
            Последний дайджест:{" "}
            {new Date(pref.last_reminder_sent_at).toLocaleString("ru-RU")}
          </div>
        )}
      </section>

      <section className="bg-tg-sectionBg rounded-xl p-4 mt-4">
        <div className="text-sm font-medium mb-1">Умные уведомления</div>
        <div className="text-xs text-tg-hint mb-3">
          Тихие часы и порог важности — чтобы бот не отвлекал ночью и
          по мелочам.
        </div>

        <div className="text-xs text-tg-hint mb-1">
          Тихие часы (дайджест не приходит)
        </div>
        <div className="flex items-center gap-2 mb-3">
          <input
            type="time"
            value={pref?.quiet_from || ""}
            disabled={saving}
            onChange={(e) =>
              patchPref({
                quiet_from: e.target.value,
                quiet_to: pref?.quiet_to || "",
              })
            }
            className="px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          />
          <span className="text-tg-hint text-sm">—</span>
          <input
            type="time"
            value={pref?.quiet_to || ""}
            disabled={saving}
            onChange={(e) =>
              patchPref({
                quiet_from: pref?.quiet_from || "",
                quiet_to: e.target.value,
              })
            }
            className="px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          />
          {(pref?.quiet_from || pref?.quiet_to) && (
            <button
              disabled={saving}
              onClick={() => patchPref({ quiet_from: "", quiet_to: "" })}
              className="text-tg-link text-xs ml-auto"
            >
              Сбросить
            </button>
          )}
        </div>

        <div className="text-xs text-tg-hint mb-1">Порог важности</div>
        <div className="space-y-2">
          <ModeRadio
            label="Все уведомления"
            description="Получать и рутинные напоминания, и срочные."
            value="all"
            current={pref?.min_severity}
            disabled={saving}
            onClick={() => patchPref({ min_severity: "all" })}
          />
          <ModeRadio
            label="Только срочное"
            description="Пропускать рутинные дайджесты — присылать только важное."
            value="high"
            current={pref?.min_severity}
            disabled={saving}
            onClick={() => patchPref({ min_severity: "high" })}
          />
        </div>
      </section>

      {me.roles.includes("admin") && <StaffManager />}
    </div>
  );
}

function StaffManager() {
  const [data, setData] = useState<StaffListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState("operator");
  const [tgId, setTgId] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      setData(await api.get<StaffListResponse>("/api/staff"));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = parseInt(tgId.trim(), 10);
    if (!Number.isFinite(id) || !name.trim()) {
      setError("Укажите Telegram ID (число) и имя.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/staff", {
        role,
        telegram_id: id,
        name: name.trim(),
      });
      setTgId("");
      setName("");
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (m: StaffMember) => {
    try {
      if (m.is_active) {
        await api.del(`/api/staff/${m.role}/${m.id}`);
      } else {
        await api.put(`/api/staff/${m.role}/${m.id}`, { is_active: true });
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const rename = async (m: StaffMember) => {
    const next = window.prompt(`Новое имя для ${m.name}:`, m.name);
    if (next == null || !next.trim()) return;
    try {
      await api.put(`/api/staff/${m.role}/${m.id}`, { name: next.trim() });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const inputCls =
    "w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm";
  const roleKeys = data ? Object.keys(data.roles) : [];

  return (
    <section className="bg-tg-sectionBg rounded-xl p-4 mt-4">
      <div className="text-sm font-medium mb-1">👥 Сотрудники и роли</div>
      <div className="text-xs text-tg-hint mb-3">
        Назначение и снятие ролей. Администраторы задаются в конфигурации
        сервера (.env) и здесь не редактируются.
      </div>

      {error && (
        <div className="mb-2 p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}

      <form onSubmit={add} className="space-y-2 mb-4">
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className={inputCls}
        >
          {roleKeys.map((k) => (
            <option key={k} value={k}>
              {data!.roles[k]}
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <input
            value={tgId}
            onChange={(e) => setTgId(e.target.value)}
            placeholder="Telegram ID"
            inputMode="numeric"
            className={inputCls}
          />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Имя сотрудника"
            className={inputCls}
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="w-full px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
        >
          Назначить на роль
        </button>
      </form>

      {data === null ? (
        <div className="text-tg-hint text-sm">Загрузка…</div>
      ) : (
        <>
          {roleKeys.map((rk) => {
            const inRole = data.members.filter((m) => m.role === rk);
            return (
              <div key={rk} className="mb-3">
                <div className="text-xs uppercase tracking-wider text-tg-hint mb-1">
                  {data.roles[rk]}
                </div>
                {inRole.length === 0 ? (
                  <div className="text-xs text-tg-hint">— нет —</div>
                ) : (
                  <div className="space-y-1">
                    {inRole.map((m) => (
                      <div
                        key={`${m.role}-${m.id}`}
                        className="flex items-baseline justify-between gap-2 text-sm"
                      >
                        <div className="min-w-0">
                          <span
                            className={
                              m.is_active ? "" : "text-tg-hint line-through"
                            }
                          >
                            {m.name}
                          </span>
                          <span className="text-xs text-tg-hint">
                            {" "}
                            · {m.telegram_id}
                          </span>
                        </div>
                        <div className="flex gap-3 shrink-0">
                          <button
                            onClick={() => rename(m)}
                            className="text-tg-link text-xs"
                          >
                            Имя
                          </button>
                          <button
                            onClick={() => toggle(m)}
                            className={
                              "text-xs " +
                              (m.is_active
                                ? "text-tg-destructive"
                                : "text-tg-link")
                            }
                          >
                            {m.is_active ? "Снять" : "Вернуть"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div className="mt-3 pt-3 border-t border-tg-secondaryBg">
            <div className="text-xs uppercase tracking-wider text-tg-hint mb-1">
              Администраторы (только чтение — задаются в .env)
            </div>
            <div className="text-sm">
              {data.admin_telegram_ids.length
                ? data.admin_telegram_ids.join(", ")
                : "—"}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function ModeRadio({
  label,
  description,
  value,
  current,
  disabled,
  onClick,
}: {
  label: string;
  description: string;
  value: string;
  current?: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const active = current === value;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={
        "w-full text-left px-3 py-2.5 rounded-lg border transition-colors disabled:opacity-50 " +
        (active
          ? "border-tg-accentText bg-tg-accentText/10"
          : "border-tg-secondaryBg bg-tg-secondaryBg/30")
      }
    >
      <div className="flex items-center gap-2">
        <span
          className={
            "inline-block w-3 h-3 rounded-full border-2 " +
            (active ? "border-tg-accentText bg-tg-accentText" : "border-tg-hint")
          }
        />
        <span className="font-medium text-sm">{label}</span>
      </div>
      <div className="text-[11px] text-tg-hint mt-1 leading-snug">
        {description}
      </div>
    </button>
  );
}

function Item({ ok, text }: { ok: boolean; text: string }) {
  return (
    <li className="flex gap-2 items-center">
      <span>{ok ? "✅" : "—"}</span>
      <span className={ok ? "" : "text-tg-hint line-through"}>{text}</span>
    </li>
  );
}
